import type { FloraUserPreferences } from '../../../../api/agents-client/agent-client.type';

/**
 * Determines whether the user's Flora preferences are effectively unset, used to
 * decide whether to nudge them to fill the preferences page. Treats whitespace-only
 * text fields and empty/nullish important-objects lists as empty.
 *
 * @param {FloraUserPreferences | undefined} preferences - The fetched preferences, or undefined while loading
 * @returns {boolean} True when no preference field carries meaningful content
 */
function arePreferencesEmpty(preferences: FloraUserPreferences | undefined): boolean {
  if (!preferences) return false;
  const hasName = Boolean(preferences.preferred_name?.trim());
  const hasRole = Boolean(preferences.role?.trim());
  const hasGeneralPreferences = Boolean(preferences.general_preferences?.trim());
  const hasImportantObjects = (preferences.important_objects?.length ?? 0) > 0;
  return !hasName && !hasRole && !hasGeneralPreferences && !hasImportantObjects;
}

/**
 * Determines the current time of day based on the hour.
 *
 * @returns {'morning' | 'afternoon' | 'evening'} The time of day category.
 */
function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

/**
 * Returns a randomized greeting based on the current time of day,
 * optionally personalized with the user's first name.
 *
 * @param {string} [firstName] - The user's first name for personalization.
 * @returns {string} A randomly selected greeting string.
 */
function getGreeting(firstName?: string): string {
  const time = getTimeOfDay();

  const withName: Record<string, string[]> = {
    morning: [
      `Good morning, ${firstName}`,
      `Morning, ${firstName}. What are we tackling?`,
      `Rise and shine, ${firstName}`,
      `Hey ${firstName}, let's get started`,
      `Fresh day, ${firstName}. What's the plan?`,
      `Top of the morning, ${firstName}`,
    ],
    afternoon: [
      `Good afternoon, ${firstName}`,
      `Hey ${firstName}, what's on your mind?`,
      `Afternoon, ${firstName}. What can I dig into?`,
      `What's next, ${firstName}?`,
      `Hey ${firstName}, let's make progress`,
      `${firstName}, what are we solving today?`,
    ],
    evening: [
      `Good evening, ${firstName}`,
      `Hey ${firstName}, wrapping up the day?`,
      `Evening, ${firstName}. How can I help?`,
      `Still at it, ${firstName}? Let's go`,
      `Hey ${firstName}, what's on the agenda?`,
      `Burning the midnight oil, ${firstName}?`,
    ],
  };

  const withoutName: Record<string, string[]> = {
    morning: [
      'Good morning',
      'What can I help with today?',
      'Ready when you are',
      "Let's make today count",
      "What's on the agenda?",
      'Fresh start. What do you need?',
    ],
    afternoon: [
      'Good afternoon',
      'What are we working on?',
      'How can I help?',
      "What's next on the list?",
      "Let's keep the momentum going",
      'What do you need?',
    ],
    evening: [
      'Good evening',
      'What can I help with tonight?',
      'How can I help?',
      'Finishing strong?',
      'What do you need before end of day?',
      "Let's wrap things up",
    ],
  };

  const pool = firstName ? withName[time] : withoutName[time];
  return pool[Math.floor(Math.random() * pool.length)];
}

export { arePreferencesEmpty, getGreeting, getTimeOfDay };
